/**
 * GRAMATYKA UKŁADU — zamknięty, serializowalny opis etykiety, który tenant
 * może edytować w panelu, a który NIE potrafi wyrazić logiki.
 *
 * Po co osobny typ, skoro jest już `ir.ts`. IR jest wynikiem `build(vars)` —
 * węzły mają już PODSTAWIONE wartości, więc IR nie da się edytować w panelu
 * i zapisać: zapisany IR to fotografia jednego zestawu zmiennych. Układ jest
 * o krok wcześniej: zamiast `text: 'Partia: LOT-2026-001'` niesie
 * `tekst: [{lit:'Partia: '},{var:'batch_number'}]`. Interpreter
 * (`interpreter.ts`) zamienia układ + zmienne na IR, a dalej wszystko jedzie
 * niezmienioną drogą: te same enkodery, ten sam facsimile, ten sam PDF.
 *
 * TRZY DECYZJE, KTÓRE TRZYMAJĄ TEN PLIK W RYZACH
 *
 * 1. `tekst`/`dane` NIE SĄ STRINGIEM. Gdyby `{{batch_number}}` mieszkało
 *    wewnątrz `TextNode.text`, układ NIEPODSTAWIONY byłby strukturalnie
 *    poprawnym IR — i błąd w przebiegu podstawiającym wydrukowałby na
 *    naklejce literalne `{{ batch_number }}` zamiast rzucić. Tu układ
 *    niepodstawiony jest dla enkodera typem nieprzedstawialnym: enkoder
 *    przyjmuje `PrintNode`, a `LayoutNode` nim nie jest.
 *
 * 2. `Meta` (czyli `id`) jest wpięta w KAŻDY wariant, także w wiersze
 *    powtórzenia. Gdyby identyfikator siedział tylko na członie unii
 *    najwyższego poziomu, dzieci wiersza tabeli byłyby nieadresowalne dla
 *    zaznaczenia, historii i warstw — czyli edytor nie umiałby ich kliknąć.
 *
 * 3. Gramatyka jest DOMKNIĘTA w obie strony. `RODZAJE` ma typ
 *    `Record<PrintNode['kind'], true>`, więc nowy węzeł w `ir.ts` nie
 *    skompiluje się, dopóki nie nauczy się go edytor. Ten jeden wiersz
 *    zastępuje konwencję „pamiętaj, żeby dopisać".
 *
 * Czego tu NIE MA i nie będzie: pętli, warunków, filtrów, arytmetyki.
 * To nie jest drugi Liquid. Zbiór rzeczy zapisywalnych w układzie jest
 * dokładnie zbiorem rzeczy, które da się narysować myszą.
 */

import type { PrintNode } from '../ir';
import type { PrintMedia } from '../types';

/**
 * Wersja GRAMATYKI (nie wzoru i nie rewizji odstępstwa — patrz niżej).
 * Podbicie znaczy: zapisane układy wymagają przejścia migratorem.
 */
export const LAYOUT_VERSION = 1;

/**
 * Token wyrażenia. Trzy warianty i ani jednego więcej:
 *   • `lit`   — tekst wpisany przez człowieka,
 *   • `var`   — zmienna wzoru (`PrintTemplateDef.variables`),
 *   • `pole`  — pole BIEŻĄCEJ pozycji listy; legalne WYŁĄCZNIE wewnątrz
 *               `zrodlo.rodzaj === 'lista'`, bo poza wierszem nie istnieje
 *               „bieżąca pozycja" i token nie miałby do czego się odnieść.
 */
export type Token =
  | { lit: string }
  | { var: string }
  | { pole: string }
  /**
   * Chwila wydruku. Czwarty wariant, i to nie jest rozluźnienie reguły „trzy
   * i ani jednego więcej" — to jedyna wartość, której NIE MA w danych zadania
   * i nie da się jej podać zmienną: każdy wołający toru druku musiałby ją
   * wstawiać sam, a etykieta drukowana z tabletu, z terminala i ze zdarzenia
   * NATS miałaby trzy różne formaty daty.
   *
   * Nie ma tu dowolnego wzorca formatu (`YYYY-MM-DD HH:mm`) świadomie:
   * wzorzec przyjeżdżający z układu tenanta jest kodem, który trzeba by
   * walidować i który na drukarce termicznej rozjeżdża szerokość kolumny.
   * Trzy kształty pokrywają to, po co po datę się sięga.
   */
  | { teraz: KsztaltTeraz };

/** Kształty chwili wydruku — zamknięty zbiór, bo format to nie preferencja. */
export const KSZTALTY_TERAZ = ['data', 'godzina', 'data_godzina'] as const;

export type KsztaltTeraz = (typeof KSZTALTY_TERAZ)[number];

/** Ludzkie nazwy kształtów chwili — panel nie pokazuje `data_godzina`. */
export const NAZWA_KSZTALTU_TERAZ: Record<KsztaltTeraz, string> = {
  data: 'Bieżąca data',
  godzina: 'Bieżąca godzina',
  data_godzina: 'Bieżąca data i godzina',
};

/** Wyrażenie = sklejenie tokenów w tej kolejności. Bez separatorów, bez logiki. */
export type Wyrazenie = Token[];

/** Metadane edytora — wspólne dla każdego węzła, także zagnieżdżonego. */
export interface MetaWezla {
  /** Stabilny identyfikator w obrębie układu: zaznaczenie, historia, warstwy. */
  id: string;
  /** Nazwa w panelu warstw. Brak = panel pokazuje nazwę rodzaju. */
  nazwa?: string;
  /** Zablokowany: widoczny i drukowany, ale nieprzesuwalny myszą. */
  zablokowany?: boolean;
  /** Ukryty: NIE trafia do IR, więc nie drukuje się i nie widać go w podglądzie. */
  ukryty?: boolean;
}

/** Skąd blok obrazu bierze bajty. */
export type ZrodloObrazu =
  /**
   * Zasób wgrany przez tenanta — etap E6. `assetId` = `print_assets.id` (UUID
   * kartoteki), NIE sha256 z bucketu: skrót jest nazwą obiektu i kluczem
   * deduplikacji po stronie serwisu, a układ wskazuje WIERSZ kartoteki, po
   * którym print-core czyta bajty (`repo.poId`) i który edytor zapisuje z listy.
   */
  | { rodzaj: 'zasob'; assetId: string }
  /** Logo organizacji z kartoteki `organizations` — bez duplikowania pliku. */
  | { rodzaj: 'logo_organizacji' }
  /** Zmienna wzoru o `kind: 'image'` (obraz przekazany przez wołającego). */
  | { rodzaj: 'zmienna'; var: string };

/** Skąd powtórzenie bierze wiersze. */
export type ZrodloWierszy =
  /**
   * Wiersze wpisane wprost. Jedyny wariant, w jakim da się zasiać DOWOLNY
   * dzisiejszy wzór — dlatego zasiew ma się do czego wycofać (`zKompozycji.ts`).
   */
  | { rodzaj: 'literalne'; wiersze: LayoutNode[][] }
  /**
   * Tekst wielolinijkowy — odpowiednik `tekstWielolinijkowy()` z katalogu.
   * Interpreter woła `zawijaj()` z `ir.ts`, czyli TO SAMO łamanie, którego
   * używają wzory: ZPL, EPL i PDF łamią w identycznych miejscach.
   */
  | {
      rodzaj: 'zawijanie';
      xMm: number;
      tekst: Wyrazenie;
      maxChars: number;
      maxLines: number;
      sizePt?: number;
      bold?: boolean;
    }
  /**
   * Tabela danych — jedyna rzecz, której IR nie wyraża bez kodu. `var`
   * wskazuje zmienną `kind: 'list'`; wewnątrz `wiersz` legalny jest `{pole}`.
   */
  | { rodzaj: 'lista'; var: string; maxWierszy: number; wiersz: LayoutNode[] };

/**
 * Węzeł układu. Kształt lustrzany wobec `PrintNode` z `ir.ts` z jedną
 * systematyczną różnicą: wszędzie, gdzie IR ma gotowy `string`, układ ma
 * `Wyrazenie`. Jednostki bez zmian — milimetry i punkty; piksel nie istnieje
 * w zapisanym modelu, a doty zna wyłącznie enkoder (`mmNaDoty`).
 */
export type LayoutNode =
  | (MetaWezla & {
      kind: 'text';
      xMm: number;
      yMm: number;
      tekst: Wyrazenie;
      sizePt?: number;
      bold?: boolean;
      /**
       * Krój — legalny TYLKO we wzorze, którego wszystkie cele idą przez
       * HTML/PDF. Wzór z celem termicznym dostaje `kroj_nieobslugiwany`
       * z walidatora, bo drukarka etykiet ma własne fonty urządzenia i nie
       * ma jak tego dotrzymać.
       */
      kroj?: 'ui' | 'serif' | 'mono';
      maxWidthMm?: number;
      align?: 'left' | 'center' | 'right';
    })
  | (MetaWezla & {
      kind: 'barcode';
      xMm: number;
      yMm: number;
      dane: Wyrazenie;
      symbology: 'code128' | 'ean13';
      heightMm: number;
      showText?: boolean;
    })
  | (MetaWezla & { kind: 'qr'; xMm: number; yMm: number; dane: Wyrazenie; sizeMm: number })
  | (MetaWezla & {
      kind: 'line';
      xMm: number;
      yMm: number;
      x2Mm: number;
      y2Mm: number;
      thicknessMm?: number;
    })
  | (MetaWezla & {
      kind: 'box';
      xMm: number;
      yMm: number;
      widthMm: number;
      heightMm: number;
      thicknessMm?: number;
    })
  | (MetaWezla & {
      kind: 'image';
      xMm: number;
      yMm: number;
      widthMm: number;
      heightMm: number;
      zrodlo: ZrodloObrazu;
      alt?: string;
    })
  | (MetaWezla & { kind: 'repeat'; yMm: number; stepMm: number; zrodlo: ZrodloWierszy });

/**
 * DOMKNIĘCIE GRAMATYKI. Typ jest `Record<PrintNode['kind'], true>`, więc
 * dołożenie węzła do `ir.ts` bez wpisu tutaj to błąd KOMPILACJI, a nie
 * odkrycie na hali. Test `print-uklad.test.ts` domyka drugą stronę: każdy
 * klucz stąd musi być obsłużony przez interpreter i walidator.
 */
export const RODZAJE: Record<PrintNode['kind'], true> = {
  text: true,
  barcode: true,
  qr: true,
  line: true,
  box: true,
  image: true,
  repeat: true,
};

/** Lista rodzajów w stabilnej kolejności — dla panelu, dokumentacji i snapshotu. */
export const RODZAJE_LISTA = Object.keys(RODZAJE) as PrintNode['kind'][];

/**
 * Czy `v` jest rodzajem węzła z gramatyki.
 *
 * NIE `v in RODZAJE`. Operator `in` przechodzi łańcuch prototypów, więc
 * `'constructor' in RODZAJE` daje `true` — układ z `kind: "constructor"`
 * przechodził sprawdzenie rodzaju i wysadzał walidator `TypeError`-em
 * z `obrysWezla` (zwracało `undefined`, bo `switch` takiego przypadku nie zna).
 * To samo dla `toString`, `valueOf`, `hasOwnProperty` i `__proto__` — pięć
 * gotowych kluczy w każdym JSON-ie, jaki tenant przyśle.
 */
export function jestRodzaj(v: unknown): v is PrintNode['kind'] {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(RODZAJE, v);
}

/** Ludzkie nazwy rodzajów — paleta w panelu nie pokazuje `kind`. */
export const NAZWA_RODZAJU: Record<PrintNode['kind'], string> = {
  text: 'Tekst',
  barcode: 'Kod kreskowy',
  qr: 'Kod QR',
  line: 'Linia',
  box: 'Ramka',
  image: 'Obraz',
  repeat: 'Powtórzenie',
};

/**
 * Zapisany układ odstępstwa tenanta.
 *
 * TRZY NIEZALEŻNE LICZBY WERSJI, każda o czym innym — mylenie ich jest
 * najłatwiejszym błędem w tym module:
 *   • `layoutVersion`        — wersja GRAMATYKI (ten plik),
 *   • `baseTemplateVersion`  — `PrintTemplateDef.version` w chwili zasiewu;
 *                              rozjazd z katalogiem = „wymaga przeglądu",
 *   • `print_templates.version` (w bazie, nie tutaj) — numer REWIZJI zapisu.
 */
export interface PrintLayout {
  layoutVersion: number;
  /** Slug wzoru z katalogu — układ jest zawsze odstępstwem OD czegoś. */
  slug: string;
  baseTemplateVersion: number;
  /**
   * KOPIA `def.media`, nie własność układu. Walidator odrzuca rozjazd:
   * zmiana rozmiaru etykiety w edytorze przy katalogu mówiącym 100×60 mm
   * to fizycznie zła naklejka, a nie preferencja użytkownika.
   */
  media: PrintMedia;
  /** Ustawienia płótna — nie wpływają na wydruk. */
  edytor?: { siatkaMm: number; przyciaganie: boolean };
  nodes: LayoutNode[];
}

/** Zaokrąglenie współrzędnej do 0,01 mm — poniżej i tak zaokrągla `mm()` w facsimile. */
export function zaokr(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Czy token jest wiązaniem zmiennej wzoru. */
export function jestVar(t: Token): t is { var: string } {
  return typeof (t as { var?: unknown }).var === 'string';
}

/** Czy token jest odwołaniem do pola bieżącej pozycji listy. */
export function jestPole(t: Token): t is { pole: string } {
  return typeof (t as { pole?: unknown }).pole === 'string';
}

/** Czy token jest literałem. */
export function jestLit(t: Token): t is { lit: string } {
  return typeof (t as { lit?: unknown }).lit === 'string';
}

/** Czy token jest chwilą wydruku — i czy jej kształt jest ze zbioru. */
export function jestTeraz(t: Token): t is { teraz: KsztaltTeraz } {
  const v = (t as { teraz?: unknown }).teraz;
  return typeof v === 'string' && (KSZTALTY_TERAZ as readonly string[]).includes(v);
}

/**
 * KSZTAŁT GRAMATYKI JAKO WARTOŚĆ — domknięty przez kompilator w obie strony.
 *
 * `Record<Exclude<keyof Wariant, 'kind'>, 0>` wymaga KAŻDEGO pola wariantu
 * (także opcjonalnego), a `satisfies` zabrania pól nadmiarowych. Dopisanie
 * `obrot?: number` do wariantu `text` przestaje się więc kompilować, dopóki
 * nie pojawi się tutaj — a wtedy zmianę widzi snapshot gramatyki i żąda
 * podbicia `LAYOUT_VERSION`.
 *
 * Bez tego snapshot czytał ATRAPĘ z pliku testowego i miał tokeny oraz źródła
 * wpisane na sztywno: pilnował sam siebie. Zmierzone — dopisanie pola do
 * wariantu, nowego członu do unii `Token` i nowego źródła wierszy przechodziło
 * przy 64/64 zielonych i czystym `tsc`.
 */
type PolaWariantu<K extends PrintNode['kind']> = Record<
  Exclude<keyof Extract<LayoutNode, { kind: K }>, 'kind'>,
  0
>;

export const POLA_RODZAJU = {
  text: {
    id: 0,
    nazwa: 0,
    zablokowany: 0,
    ukryty: 0,
    xMm: 0,
    yMm: 0,
    tekst: 0,
    sizePt: 0,
    bold: 0,
    kroj: 0,
    maxWidthMm: 0,
    align: 0,
  },
  barcode: {
    id: 0,
    nazwa: 0,
    zablokowany: 0,
    ukryty: 0,
    xMm: 0,
    yMm: 0,
    dane: 0,
    symbology: 0,
    heightMm: 0,
    showText: 0,
  },
  qr: { id: 0, nazwa: 0, zablokowany: 0, ukryty: 0, xMm: 0, yMm: 0, dane: 0, sizeMm: 0 },
  line: {
    id: 0,
    nazwa: 0,
    zablokowany: 0,
    ukryty: 0,
    xMm: 0,
    yMm: 0,
    x2Mm: 0,
    y2Mm: 0,
    thicknessMm: 0,
  },
  box: {
    id: 0,
    nazwa: 0,
    zablokowany: 0,
    ukryty: 0,
    xMm: 0,
    yMm: 0,
    widthMm: 0,
    heightMm: 0,
    thicknessMm: 0,
  },
  image: {
    id: 0,
    nazwa: 0,
    zablokowany: 0,
    ukryty: 0,
    xMm: 0,
    yMm: 0,
    widthMm: 0,
    heightMm: 0,
    zrodlo: 0,
    alt: 0,
  },
  repeat: { id: 0, nazwa: 0, zablokowany: 0, ukryty: 0, yMm: 0, stepMm: 0, zrodlo: 0 },
} satisfies { [K in PrintNode['kind']]: PolaWariantu<K> };

/** Klucze `MetaWezla` — pole wspólne wszystkim wariantom. */
export const KLUCZE_META = { id: 0, nazwa: 0, zablokowany: 0, ukryty: 0 } satisfies Record<
  keyof MetaWezla,
  0
>;

/** Człony unii `ZrodloWierszy` — nowe źródło musi tu być, inaczej `tsc` protestuje. */
export const RODZAJE_ZRODEL_WIERSZY = {
  literalne: 0,
  zawijanie: 0,
  lista: 0,
} satisfies Record<ZrodloWierszy['rodzaj'], 0>;

/** Człony unii `ZrodloObrazu`. */
export const RODZAJE_ZRODEL_OBRAZU = {
  zasob: 0,
  logo_organizacji: 0,
  zmienna: 0,
} satisfies Record<ZrodloObrazu['rodzaj'], 0>;

/** Klucze członów unii `Token` (`Token extends …` rozdziela się po członach). */
type KluczTokena = Token extends infer T ? keyof T : never;

/** Człony gramatyki tokenów — nowy człon (np. `{fn, arg}`) musi tu być. */
export const KLUCZE_TOKENA = { lit: 0, var: 0, pole: 0, teraz: 0 } satisfies Record<KluczTokena, 0>;

/** Węzeł układu razem ze ścieżką, pod którą leży — konwencja `BladUkladu.sciezka`. */
export interface WezelZeSciezka {
  node: LayoutNode;
  sciezka: string;
}

/**
 * KAŻDY węzeł układu, także dzieci powtórzeń — jeden kanoniczny obchód drzewa.
 *
 * Powstało z pomiaru, nie z porządkowania. Bramka E6 w print-core („źródło
 * obrazu inne niż `zmienna` nie ma prawa się opublikować") chodziła po
 * `layout.nodes` najwyższego poziomu, podczas gdy walidator gramatyki dopuszcza
 * `image` na każdej głębokości. Ten sam węzeł `{kind:'image', zrodlo:{rodzaj:
 * 'zasob'}}` na wierzchu był odrzucany 422-ką, a schowany w wierszu powtórzenia
 * — publikowany i drukowany jako pusta ramka. Logo w wierszu nagłówka tabeli
 * jest najzwyklejszym miejscem na logo, więc to nie był przypadek brzegowy.
 *
 * Rekurencja mieszkała już wtedy w czterech miejscach (`walidujWezel`,
 * `obrysyUkladu`, `zmienneWezla`, `zmienneUkladu`) i piąta kopia w print-core
 * byłaby piątą okazją do dokładnie tej pomyłki. Ścieżki są identyczne z tymi,
 * które składa walidator, żeby panel podświetlił ten sam węzeł.
 */
export function* wezlyZeSciezkami(
  layout: PrintLayout,
  prefiks = 'nodes'
): Generator<WezelZeSciezka> {
  function* zejdz(node: LayoutNode, sciezka: string): Generator<WezelZeSciezka> {
    yield { node, sciezka };
    if (node.kind !== 'repeat') return;
    switch (node.zrodlo.rodzaj) {
      case 'literalne':
        for (const [i, wiersz] of node.zrodlo.wiersze.entries())
          for (const [j, dziecko] of wiersz.entries())
            yield* zejdz(dziecko, `${sciezka}.zrodlo.wiersze[${i}][${j}]`);
        return;
      case 'lista':
        for (const [j, dziecko] of node.zrodlo.wiersz.entries())
          yield* zejdz(dziecko, `${sciezka}.zrodlo.wiersz[${j}]`);
        return;
      case 'zawijanie':
        return;
    }
  }

  for (const [i, node] of layout.nodes.entries()) yield* zejdz(node, `${prefiks}[${i}]`);
}
